import React from 'react';
import type { DashboardMetrics } from '../../hooks/useDashboard';
import { formatDate, getTrinidadTime } from '../../utils/dateFormatting';
import { Card } from '../ui/Card';

interface LiveFeedProps {
  feed: DashboardMetrics['live_feed'];
  loading?: boolean;
}

export const LiveFeed: React.FC<LiveFeedProps> = ({ feed, loading }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = getTrinidadTime();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return formatDate(date);
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'placed':
        return '🆕';
      case 'accepted':
        return '✅';
      case 'rejected':
        return '❌';
      case 'cancelled':
        return '🚫';
      case 'completed':
        return '🎉';
      case 'payment_failed':
        return '💳';
      default:
        return '📦';
    }
  };

  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case 'placed':
        return 'bg-blue-100 text-blue-700';
      case 'accepted':
        return 'bg-green-100 text-green-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      case 'cancelled':
        return 'bg-orange-100 text-orange-700';
      case 'completed':
        return 'bg-emerald-100 text-emerald-700';
      case 'payment_failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getEventLabel = (eventType: string) => {
    switch (eventType) {
      case 'placed':
        return 'New Order';
      case 'accepted':
        return 'Accepted';
      case 'rejected':
        return 'Rejected';
      case 'cancelled':
        return 'Cancelled';
      case 'completed':
        return 'Completed';
      case 'payment_failed':
        return 'Payment Failed';
      default:
        return eventType;
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-2">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (!feed || feed.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-xl font-bold text-heading mb-4">📡 Live Feed</h3>
        <p className="text-muted text-center py-8">No recent activity</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-heading">📡 Live Feed</h3>
        <span className="flex items-center text-xs text-muted">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></span>
          Live
        </span>
      </div>
      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {feed.map((event) => (
          <div
            key={event.id}
            className="flex items-start space-x-3 p-3 bg-surface rounded-xl border border-border hover:border-brand-200 transition-colors"
          >
            <div className="text-2xl">{getEventIcon(event.event_type)}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <span className={`badge ${getEventColor(event.event_type)} text-xs`}>
                  {getEventLabel(event.event_type)}
                </span>
                <span className="text-xs font-mono text-muted">#{event.order_number}</span>
              </div>
              <p className="font-semibold text-heading text-sm truncate">{event.restaurant_name}</p>
              <div className="flex items-center space-x-2 mt-1">
                <span className="text-xs text-muted">📍 {event.customer_area}</span>
                <span className="text-xs text-muted">•</span>
                <span className="text-xs font-semibold text-success-600">
                  {formatCurrency(event.total)}
                </span>
              </div>
              {event.reason && (
                <p className="text-xs text-danger-600 mt-1">Reason: {event.reason}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-muted whitespace-nowrap">
                {formatTimestamp(event.timestamp)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
